// Client-safe forum constants and pure helpers. Contains NO server-only imports
// (no mongoose / db / security), so it is safe to import into client components.

export const CATEGORIES = [
  'Roblox',
  'Help',
  'General',
] as const;

export type Category = (typeof CATEGORIES)[number];

export function isValidCategory(c: unknown): c is Category {
  return typeof c === 'string' && (CATEGORIES as readonly string[]).includes(c);
}

// Discourse-style category swatch colors.
export const CATEGORY_COLORS: Record<string, string> = {
  Roblox: '#e84c3d',
  Help: '#e67e22',
  General: '#95a5a6',
};

export function catColor(category: string): string {
  return CATEGORY_COLORS[category] || '#8b95f2';
}

export const FORUM_LIMITS = {
  titleMax: 140,
  bodyMax: 20000,
  postBodyMax: 10000,
  maxTags: 6,
  tagMax: 24,
  maxAttachments: 8,
};

export const SIZE_CAPS = {
  image: 8 * 1024 * 1024,
  video: 100 * 1024 * 1024,
  file: 25 * 1024 * 1024,
} as const;

export const ALLOWED_IMAGE = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
export const ALLOWED_VIDEO = ['video/mp4', 'video/webm', 'video/quicktime'];

export type Kind = 'image' | 'video' | 'file';

export function kindForContentType(ct: string): Kind {
  if (ALLOWED_IMAGE.includes(ct)) return 'image';
  if (ALLOWED_VIDEO.includes(ct)) return 'video';
  return 'file';
}

// Strips path components, control chars, and unsafe chars; caps length. Pure.
const CONTROL_CHARS = new RegExp(
  '[' + String.fromCharCode(0) + '-' + String.fromCharCode(31) + ']',
  'g'
);
export function sanitizeFilename(name: unknown): string {
  let s = typeof name === 'string' ? name : 'file';
  s = s.split(/[\\/]/).pop() || 'file';
  s = s.replace(CONTROL_CHARS, '').replace(/[^\w.\-() ]+/g, '_').trim();
  if (!s) s = 'file';
  return s.length > 120 ? s.slice(-120) : s;
}

export function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
